/**
 * PageTemplate — a blank starter page. Copy this file to create a new page.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * HOW TO CREATE A NEW PAGE
 * ─────────────────────────────────────────────────────────────────────────
 * 1. Copy this file to src/pages/<YourPage>.tsx and rename the component.
 *
 * 2. Use the design-system components, NOT @mantine directly:
 *        import { Stack, Title, Text, Card, Button } from '@/components/ui';
 *    (advanced widgets live in '@/components/advanced', SDK-bound pieces in
 *     '@/components/ccms-components'.)
 *
 * 3. Do NOT wrap the page in your own <Container size="...">. The shell
 *    (MainLayout) already applies the global Layout Preferences from
 *    Settings → Layout Preferences (Content Width + Horizontal Padding) to
 *    every page. Just return a <Stack> (or your content) and it will conform.
 *
 * 4. Register the route in src/App.tsx (protected, inside the app shell):
 *        import YourPage from './pages/YourPage';
 *        <Route path="/your-page" element={protectedPage(<YourPage />)} />
 *
 * 5. Add a sidebar entry in src/layouts/components/MainSidebar.tsx:
 *        { label: 'Your Page', to: '/your-page' },   // in `primaryLinks`
 *
 * 6. Load data with the SDK (example):
 *        import { useHttpClient } from '@actschurch/ccms-sdk';
 *        import { getApiUrl } from '@/config/ccms';
 *        const http = useHttpClient();
 *        // const res = await http.get(getApiUrl('users', '/me'));
 *    Requests flow through the local proxy (Vite :5176 → proxy :5177 → backend).
 * ─────────────────────────────────────────────────────────────────────────
 */

import { Stack, Title, Text, Card, Group, Button, Badge } from '@/components/ui';

export default function PageTemplate() {
  return (
    <Stack gap="xl">
      {/* Page header */}
      <div>
        <Group gap="sm" align="center">
          <Title order={2}>Page Template</Title>
          <Badge variant="light" color="blue">
            starter
          </Badge>
        </Group>
        <Text c="dimmed" size="sm">
          Copy <Text span fw={600}>src/pages/PageTemplate.tsx</Text> to start a new page. See the
          comment block at the top of the file for the full checklist.
        </Text>
      </div>

      {/* Content */}
      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Title order={4}>Your content goes here</Title>
          <Text size="sm" c="dimmed">
            This page intentionally uses no <Text span fw={600}>{'<Container>'}</Text> of its own —
            width and horizontal padding come from Settings → Layout Preferences, so it conforms to
            the global layout automatically.
          </Text>
          <Group>
            <Button>Primary action</Button>
            <Button variant="light">Secondary</Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
